import type { PostResumo } from "@/lib/blog.functions";
// Portado de src/pages/tratamentos/longevidade.astro (site Astro).
import { Hero } from "@/components/itv/tratamento/Hero";
import { Pilares } from "@/components/itv/tratamento/Pilares";
import { Dores } from "@/components/itv/tratamento/Dores";
import { CardsIlustrado } from "@/components/itv/tratamento/CardsIlustrado";
import { Medico } from "@/components/itv/tratamento/Medico";
import { Faq } from "@/components/itv/tratamento/Faq";
import { Conteudos } from "@/components/itv/tratamento/Conteudos";
import { CtaPratico } from "@/components/itv/tratamento/CtaPratico";

const pilares = [
  { icone: "/icones/search.svg", titulo: "Sintoma e prevenção juntos", texto: "Quem chega pelo cansaço e quem chega pensando no envelhecimento recebem a mesma avaliação completa." },
  { icone: "/icones/shield.svg", titulo: "Plano de longo prazo", texto: "Os marcadores certos, monitorados na frequência certa, com ajustes ao longo dos anos." },
  { icone: "/icones/video-cam.svg", titulo: "Onde e como", texto: "Presencial em Bauru ou por teleconsulta para todo o Brasil. Atendimento particular." },
];

const quadros = [
  { icone: "/icones/flame.svg", titulo: "Cansaço constante", texto: "Acordar cansado e depender de café para funcionar não é normal quando se repete por meses." },
  { icone: "/icones/moon.svg", titulo: "Sono que não descansa", texto: "Dormir sete horas e acordar pior é sinal de que a qualidade do sono precisa de avaliação." },
  { icone: "/icones/activity.svg", titulo: "Disposição e libido em queda", texto: "Quando vários fatores caem juntos, a investigação busca a causa comum." },
  { icone: "/icones/clipboard.svg", titulo: "Preocupação com o futuro", texto: "Perda de força, histórico familiar, exames no limite. A prevenção funciona melhor quanto antes começa." },
];

const pilaresPlano = [
  { img: "/img/longevidade/conduta-1.webp", titulo: "Qualidade do sono", texto: "Investigação das causas e tratamento. O sono influencia peso, hormônios e disposição." },
  { img: "/img/longevidade/conduta-2.webp", titulo: "Saúde metabólica", texto: "Glicose, insulina, colesterol e fígado, corrigidos cedo." },
  { img: "/img/longevidade/conduta-3.webp", titulo: "Massa muscular", texto: "Proteína, estímulo adequado e metas medidas por bioimpedância." },
  { img: "/img/longevidade/conduta-4.webp", titulo: "Frente hormonal", texto: "Quando os exames indicarem, com a investigação completa." },
  { img: "/img/longevidade/conduta-5.webp", titulo: "Mudança de hábitos", texto: "Construída aos poucos, adequada à vida real." },
  { img: "/img/longevidade/conduta-6.webp", titulo: "Suplementação", texto: "Corrigir deficiências reais, com evidência." },
];

// Nota do cliente: a frase original citava também o "Hospital Albert
// Einstein" como formação. Essa instituição não consta na lista oficial de
// credenciais e foi omitida.
const paragrafosMedico = [
  "O Dr. Thiago é o criador do Método SP, Saúde com Propósito, e este acompanhamento é a aplicação completa do método.",
  "As três especialidades registradas cobrem o que mais pesa no envelhecer bem: metabolismo, músculo e movimento. Pós-graduando em Medicina Regenerativa. Formação pela UNIFESP-EPM.",
];

// Resposta 01 do Figma; 02 a 05 redigidas a partir da copy aprovada da página.
export const faq = [
  { pergunta: "É um check-up?", resposta: "O check-up produz uma lista de exames. Aqui os resultados viram um plano, e o plano vira acompanhamento contínuo." },
  { pergunta: "Preciso estar doente para começar?", resposta: "Não. Boa parte das pessoas chega sem diagnóstico, pelo cansaço ou pela preocupação com o futuro. A prevenção funciona melhor quanto antes começa." },
  { pergunta: "Qualidade de vida e longevidade são consultas diferentes?", resposta: "Não. É o mesmo acompanhamento. Quem chega pelo sintoma de hoje e quem chega pensando nos próximos vinte anos recebem a mesma avaliação completa, porque os fatores são os mesmos." },
  { pergunta: "Esse acompanhamento é para mim?", resposta: "Se você convive com cansaço, sono ruim e queda de disposição, ou quer tratar cedo o que define o envelhecer bem, sim." },
  { pergunta: "Funciona por teleconsulta?", resposta: "Sim. A avaliação e o acompanhamento acontecem por vídeo, com os exames feitos na sua cidade, para todo o Brasil." },
];


export function Longevidade({ artigos }: { artigos: PostResumo[] }) {
  return (
    <>
      <Hero
        trilha={[{ nome: "Home", href: "/" }, { nome: "Tratamentos", href: "/tratamentos/" }, { nome: "Longevidade e qualidade de vida" }]}
        sobretitulo="TRATAMENTO"
        linha1="Longevidade e"
        linha2="qualidade de vida."
        chamada="Cansaço, sono ruim e falta de disposição têm causa. E os fatores que explicam esses sintomas são os mesmos que determinam a sua saúde daqui a vinte ou trinta anos. Este acompanhamento cuida dos dois ao mesmo tempo."
        imagem="/img/longevidade/hero.webp"
      />

      <Pilares
        sobretitulo="O ACOMPANHAMENTO"
        titulo="Energia hoje, saúde no longo prazo"
        chamada="O acompanhamento investiga o que está reduzindo sua energia e disposição, incluindo sono, metabolismo, hormônios e hábitos, e trata os fatores que definem um envelhecimento saudável: massa muscular, saúde metabólica e prevenção. É um acompanhamento contínuo, com plano de longo prazo."
        itens={pilares}
      />

      <Dores
        sobretitulo="ANTES QUE VIRE DIAGNÓSTICO"
        titulo="Cansaço e sono ruim não são o seu normal"
        chamada="Estes quadros costumam ser tratados como parte normal da rotina, mas têm causa e tratamento:"
        itens={quadros}
      />

      <CardsIlustrado
        sobretitulo="OS PILARES DO PLANO"
        titulo="Por onde a longevidade começa no seu caso"
        chamada="Conforme o que a avaliação mostrar, o plano começa pelos pilares que mais precisam de atenção:"
        cards={pilaresPlano}
      />

      <Medico paragrafos={paragrafosMedico} />
      <Faq titulo="Dúvidas sobre o acompanhamento" itens={faq} />
      <Conteudos titulo="Continue lendo sobre longevidade" artigos={artigos} />
      <CtaPratico linha1="Comece seu tratamento de" linha2="longevidade. Escolha o formato." />
    </>
  );
}
